import {
	FormDocument,
	InfoForm,
	InfoFormField,
	InfoFormXfaMatch,
	MetadataFormsFormat,
	Parameter
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import FunctionHelper from "../../../../../shared/helper/FunctionHelper";
import FormValue from "./FormValue";
import {FormFieldSubType} from "../FormFieldSubType";
import XmlHelper from "../../../../../shared/helper/XmlHelper";
import Exception from "../../../../../shared/exception/Exception";
import {Base64} from "js-base64";

const OffValue: string = "Off";

export default class FormValues implements Parameter {
	public values?: Array<FormValue>;
	public formInfo?: InfoForm;

	constructor(data: any) {
		this.values = (data.values || []).map(FormValue.fromJson);
		this.formInfo = InfoForm.fromJson(data?.formInfo);
	}

	public getFieldInfo(fullyQualifiedName: string): InfoFormField | undefined {
		return FunctionHelper.applyValue(
			this.formInfo?.acroFormFields, InfoForm.getAcroFormFieldsDefault()
		).find((value: InfoFormField) => {
			return value.fullyQualifiedName === fullyQualifiedName;
		});
	}

	public getValue(fullyQualifiedName: string): FormValue | undefined {
		return FunctionHelper.applyValue(
			this.values, FormValues.getValuesDefault()
		).find((value: FormValue) => {
			return value.fullyQualifiedName === fullyQualifiedName;
		});
	}

	public setValue(formValue: FormValue): void {
		let formValues: Array<FormValue> = FunctionHelper.applyValue(this.values, FormValues.getValuesDefault());
		let fieldInfo: InfoFormField | undefined = this.getFieldInfo(formValue.fullyQualifiedName!);

		formValues = formValues.filter((currentFormValue: FormValue) => {
			return currentFormValue.fullyQualifiedName !== formValue.fullyQualifiedName;
		});

		for (let currentFormValue of formValues.values()) {
			let currentFieldInfo: InfoFormField | undefined = this.getFieldInfo(currentFormValue.fullyQualifiedName!);
			if (currentFieldInfo?.xfaField?.match === InfoFormXfaMatch.Global &&
				currentFieldInfo?.xfaField?.name === fieldInfo?.xfaField?.name) {

				switch (currentFormValue.type) {
					case FormFieldSubType.CHECKBOX:
						currentFormValue.value = OffValue;
						break;
					case FormFieldSubType.TEXT:
					case FormFieldSubType.COMBOBOX:
					case FormFieldSubType.LISTBOX:
					case FormFieldSubType.RADIOBUTTON:
						currentFormValue.value = formValue.value;
						break;
					default:
						// unknown field type or container type
						break;
				}
			}
		}

		formValues.push(formValue);
		this.values = formValues;
	}

	public static getValuesDefault(): Array<FormValue> {
		return [];
	}

	public static fromJson(data: any): FormValues {
		if (!FunctionHelper.exists(data)) {
			return data;
		}

		return new FormValues(data);
	}

	public static fromMetadata(data?: FormDocument, formInfo?: InfoForm): FormValues {
		if (!FunctionHelper.exists(data)) {
			return new FormValues({formInfo: formInfo});
		}

		let values: Array<FormValue> = [];
		for (let field of FunctionHelper.applyValue(data?.field, FormDocument.getFieldDefault())) {
			let formValue: FormValue = FormValue.fromJson({
				type: field.subType,
				name: field.name,
				fullyQualifiedName: field.fullyQualifiedName,
				isRichtext: field.isRichText
			} as FormValue);

			switch (field.subType) {
				case FormFieldSubType.TEXT:
				case FormFieldSubType.RADIOBUTTON:
				case FormFieldSubType.COMBOBOX:
				case FormFieldSubType.CHECKBOX:
					formValue.value = XmlHelper.decode(field.content);
					break;
				case FormFieldSubType.LISTBOX:
					formValue.value = XmlHelper.decode(field.content).split(",");
					break;
				default:
					// unknown field type or container type
					continue;
			}

			values.push(formValue);
		}

		return new FormValues({values: values, formInfo: formInfo});
	}

	public toJson(): any {
		return {
			'values': this.values?.map((data) => data.toJson()),
			'formInfo': this.formInfo?.toJson()
		};
	}

	private createXPath(contextNode: Node, xpath: string): void {
		let parts: Array<string> = xpath.split("/");
		let currentPath: string = "//";
		let currentElement: Node = contextNode;

		for (let part of parts) {
			if (FunctionHelper.isEmpty(part)) {
				continue;
			}

			currentPath += part;

			let tempElement: Node | null = XmlHelper.findByXPath(contextNode, currentPath);
			if (!FunctionHelper.exists(tempElement)) {
				let matches: RegExpMatchArray | null = part.match(/".*"/g);
				if (!FunctionHelper.exists(matches) || !FunctionHelper.exists(matches![0])) {
					throw new Error("could not create xPath");
				}

				let tagName: string = XmlHelper.prepareForXmlTagName(
					matches![0].replaceAll("\"", "")
				);
				tempElement = document.createElementNS("", tagName);
				currentElement.appendChild(tempElement);
			}

			currentElement = tempElement!;
			currentPath += "/";
		}
	}

	private StaticXfaToXml(): string {
		let formData: string = Base64.decode(FunctionHelper.applyValue(this.formInfo?.value, ""));
		let xmlDoc: XMLDocument = XmlHelper.fromString(formData);

		for (let formValue of FunctionHelper.applyValue(this.values, FormValues.getValuesDefault())) {
			let fieldInfo: InfoFormField | undefined = this.getFieldInfo(formValue.fullyQualifiedName);
			let xPath: string = FunctionHelper.applyValue(
				fieldInfo?.dataXPath, InfoFormField.getDataXPathDefault()
			);

			let xmlElement: Node | null = XmlHelper.findByXPath(xmlDoc.documentElement, xPath);
			if (!FunctionHelper.exists(xmlElement) && !formValue.isRichtext) {
				// handle global field elements
				if (fieldInfo?.xfaField?.match === InfoFormXfaMatch.Global) {
					xPath = "//*[local-name()=\"" + fieldInfo!.xfaField!.name + "\"]";
					xmlElement = XmlHelper.findByXPath(xmlDoc.documentElement, xPath);

					if (!FunctionHelper.exists(xmlElement)) {
						xPath = "//*[local-name()=\"data\"]/*[local-name()=\"" + fieldInfo!.xfaField!.name + "\"]";
						this.createXPath(xmlDoc, xPath);
						xmlElement = XmlHelper.findByXPath(xmlDoc.documentElement, xPath);
					}
				} else {
					this.createXPath(xmlDoc, xPath);
					xmlElement = XmlHelper.findByXPath(xmlDoc.documentElement, xPath);
				}
			}

			if (FunctionHelper.exists(xmlElement)) {
				if (formValue.isRichtext) {
					XmlHelper.delete(xmlElement);
					continue;
				}

				let value: any = formValue.value;

				// handle global field values
				if (fieldInfo?.xfaField?.match === InfoFormXfaMatch.Global) {
					for (let field of FunctionHelper.applyValue(
						this.formInfo?.acroFormFields, InfoForm.getAcroFormFieldsDefault()
					)) {
						if (field.xfaField?.name !== fieldInfo?.xfaField?.name) {
							continue;
						}

						let fieldValue: FormValue | undefined = this.getValue(field.fullyQualifiedName!)
						if (FunctionHelper.exists(fieldValue) && fieldValue?.type === FormFieldSubType.CHECKBOX &&
							fieldValue.value !== OffValue) {
							value = formValue.value;
						}
					}
				}

				switch (formValue.type) {
					case FormFieldSubType.RADIOBUTTON:
					case FormFieldSubType.COMBOBOX:
					case FormFieldSubType.CHECKBOX:
						if (formValue.value === OffValue) {
							value = "";
						}
						break;
					case FormFieldSubType.LISTBOX:
						value = value.join(",");
						break;
				}

				xmlElement!.textContent = XmlHelper.encode(value);
			} else {
				throw new Exception("Field " + xPath + " not found");
			}
		}

		return XmlHelper.toString(xmlDoc);
	}

	private AcroformToXml(): string {
		let formData: string = Base64.decode(FunctionHelper.applyValue(this.formInfo?.value, ""));
		let xmlDoc: XMLDocument = XmlHelper.fromString(formData);

		for (let formValue of FunctionHelper.applyValue(this.values, FormValues.getValuesDefault())) {
			let fieldInfo: InfoFormField | undefined = this.getFieldInfo(formValue.fullyQualifiedName);
			let xPath: string = FunctionHelper.applyValue(
				fieldInfo?.dataXPath, InfoFormField.getDataXPathDefault()
			);

			let xmlElement: Node | null = XmlHelper.findByXPath(xmlDoc.documentElement, xPath);
			if (FunctionHelper.exists(xmlElement)) {
				if (formValue.isRichtext) {
					XmlHelper.delete(xmlElement);
					continue;
				}

				let value: any = formValue.value;

				switch (formValue.type) {
					case FormFieldSubType.RADIOBUTTON:
					case FormFieldSubType.COMBOBOX:
					case FormFieldSubType.CHECKBOX:
						if (formValue.value === "") {
							value = OffValue;
						}
						break;
					case FormFieldSubType.LISTBOX:
						value = value.join(",");
						break;
				}

				xmlElement!.textContent = value;
			} else {
				throw new Exception("Field " + xPath + " not found");
			}
		}

		return XmlHelper.toString(xmlDoc);
	}

	public toXml(formType: MetadataFormsFormat): string {
		switch (formType) {
			case MetadataFormsFormat.AcroForm:
				return this.AcroformToXml();
			case MetadataFormsFormat.StaticXFA:
				return this.StaticXfaToXml();
			default:
				throw new Exception('Can not parse formtype to xml');
		}
	}

	public clone(): FormValues {
		return FormValues.fromJson(this.toJson());
	}
}